/**
 * Currency Types
 * 
 * Types for multi-currency support across the application.
 */

export interface Currency {
  code: string; // ISO 4217 currency code (e.g., "USD", "GBP", "EUR")
  symbol: string; // Currency symbol (e.g., "$", "£", "€")
  name: string; // Full name (e.g., "US Dollar", "British Pound")
  symbolPosition: "before" | "after"; // Where to place the symbol
  decimalPlaces: number; // Number of decimal places
  thousandsSeparator: string; // Thousands separator (e.g., ",")
  decimalSeparator: string; // Decimal separator (e.g., ".")
}

export type CurrencyCode = 
  | "GBP" | "USD" | "EUR" | "JPY" | "AUD" | "CAD" | "CHF" | "CNY" 
  | "INR" | "NZD" | "ZAR" | "SEK" | "NOK" | "DKK" | "PLN" | "MXN" 
  | "BRL" | "SGD" | "HKD" | "KRW" | "THB" | "AED" | "SAR";

export const SUPPORTED_CURRENCIES: Record<string, Currency> = {
  GBP: {
    code: "GBP",
    symbol: "£",
    name: "British Pound",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  USD: {
    code: "USD",
    symbol: "$",
    name: "US Dollar",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  EUR: {
    code: "EUR",
    symbol: "€",
    name: "Euro",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  JPY: {
    code: "JPY",
    symbol: "¥",
    name: "Japanese Yen",
    symbolPosition: "before",
    decimalPlaces: 0,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  AUD: {
    code: "AUD",
    symbol: "A$",
    name: "Australian Dollar",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  CAD: {
    code: "CAD",
    symbol: "C$",
    name: "Canadian Dollar",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  CHF: {
    code: "CHF",
    symbol: "CHF",
    name: "Swiss Franc",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: "'",
    decimalSeparator: ".",
  },
  CNY: {
    code: "CNY",
    symbol: "¥",
    name: "Chinese Yuan",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  INR: {
    code: "INR",
    symbol: "₹",
    name: "Indian Rupee",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  NZD: {
    code: "NZD",
    symbol: "NZ$",
    name: "New Zealand Dollar",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  ZAR: {
    code: "ZAR",
    symbol: "R",
    name: "South African Rand",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  SEK: {
    code: "SEK",
    symbol: "kr",
    name: "Swedish Krona",
    symbolPosition: "after",
    decimalPlaces: 2,
    thousandsSeparator: " ",
    decimalSeparator: ",",
  },
  NOK: {
    code: "NOK",
    symbol: "kr",
    name: "Norwegian Krone",
    symbolPosition: "after",
    decimalPlaces: 2,
    thousandsSeparator: " ",
    decimalSeparator: ",",
  },
  DKK: {
    code: "DKK",
    symbol: "kr",
    name: "Danish Krone",
    symbolPosition: "after",
    decimalPlaces: 2,
    thousandsSeparator: ".",
    decimalSeparator: ",",
  },
  PLN: {
    code: "PLN",
    symbol: "zł",
    name: "Polish Złoty",
    symbolPosition: "after",
    decimalPlaces: 2,
    thousandsSeparator: " ",
    decimalSeparator: ",",
  },
  MXN: {
    code: "MXN",
    symbol: "$",
    name: "Mexican Peso",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  BRL: {
    code: "BRL",
    symbol: "R$",
    name: "Brazilian Real",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ".",
    decimalSeparator: ",",
  },
  SGD: {
    code: "SGD",
    symbol: "S$",
    name: "Singapore Dollar",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  HKD: {
    code: "HKD",
    symbol: "HK$",
    name: "Hong Kong Dollar",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  KRW: {
    code: "KRW",
    symbol: "₩",
    name: "South Korean Won",
    symbolPosition: "before",
    decimalPlaces: 0,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  THB: {
    code: "THB",
    symbol: "฿",
    name: "Thai Baht",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  AED: {
    code: "AED",
    symbol: "د.إ",
    name: "UAE Dirham",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  SAR: {
    code: "SAR",
    symbol: "﷼",
    name: "Saudi Riyal",
    symbolPosition: "before",
    decimalPlaces: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
};

export const DEFAULT_CURRENCY = SUPPORTED_CURRENCIES.GBP;

// Alias for backward compatibility
export const CURRENCIES = SUPPORTED_CURRENCIES;

/**
 * Get currency by code
 */
export function getCurrency(code: string): Currency {
  return SUPPORTED_CURRENCIES[code] || DEFAULT_CURRENCY;
}

/**
 * Get list of all supported currencies
 */
export function getAllCurrencies(): Currency[] {
  return Object.values(SUPPORTED_CURRENCIES);
}
